/**
 * Fixed Privacy Consent Service - No external CORS dependencies
 * 
 * This service manages privacy consent without relying on external APIs
 * that cause CORS issues and login loops.
 */

interface UserLocation {
  country?: string;
  region?: string;
  timezone?: string;
  isEU?: boolean;
  requiresConsent?: boolean;
}

export interface ConsentSettings {
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
  necessary: boolean; // Always true
}

export interface ConsentData extends ConsentSettings {
  timestamp?: string;
  version?: string;
  advertising?: boolean;
}

export interface PrivacySettings extends ConsentSettings {
  dataRetentionPeriod?: number;
  allowDataSharing?: boolean;
  allowPersonalization?: boolean;
  blockedCategories?: string[];
  frequencyCap?: number;
  showPersonalizedAds?: boolean;
  allowLocationTracking?: boolean;
  allowCrossSiteTracking?: boolean;
  allowThirdPartyData?: boolean;
  allowDataCollection?: boolean;
  allowCookies?: boolean;
}

class PrivacyConsentService {
  private static instance: PrivacyConsentService;
  private userLocation: UserLocation | null = null;
  private consentSettings: ConsentSettings | null = null;
  
  private constructor() {
    this.loadStoredConsent();
    this.detectUserLocationSafely();
  }
  
  public static getInstance(): PrivacyConsentService {
    if (!PrivacyConsentService.instance) {
      PrivacyConsentService.instance = new PrivacyConsentService();
    }
    return PrivacyConsentService.instance;
  }
  
  /**
   * Safely detect user location without external API calls
   */
  private async detectUserLocationSafely(): Promise<void> {
    try {
      // Use browser timezone to make educated guesses about location
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const language = navigator.language;
      
      console.log('🌍 PrivacyConsentService: Detecting location safely', { timezone, language });
      
      // Determine if user is likely in EU based on timezone and language
      const euTimezones = [
        'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome',
        'Europe/Madrid', 'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Vienna',
        'Europe/Stockholm', 'Europe/Helsinki', 'Europe/Warsaw', 'Europe/Prague',
        'Europe/Budapest', 'Europe/Bucharest', 'Europe/Sofia', 'Europe/Athens',
        'Europe/Dublin', 'Europe/Lisbon', 'Europe/Luxembourg', 'Europe/Copenhagen'
      ];
      
      const euLanguages = ['en-GB', 'fr', 'de', 'it', 'es', 'nl', 'sv', 'da', 'fi', 'pl', 'cs', 'hu', 'ro', 'bg', 'el', 'pt'];
      
      const isLikelyEU = euTimezones.includes(timezone) || 
                        euLanguages.some(lang => language.startsWith(lang));
      
      this.userLocation = {
        timezone,
        isEU: isLikelyEU,
        requiresConsent: isLikelyEU, // EU users require explicit consent
        country: this.guessCountryFromTimezone(timezone),
        region: this.guessRegionFromTimezone(timezone)
      };
      
      console.log('✅ PrivacyConsentService: Location detected safely', this.userLocation);

      // For non-EU users who haven't set consent yet, auto-grant all consent
      // (GDPR explicit consent is only required for EU users)
      if (!isLikelyEU && !this.consentSettings) {
        this.consentSettings = {
          necessary: true,
          analytics: true,
          marketing: true,
          functional: true,
        };
        this.saveConsentSettings();
        console.log('✅ PrivacyConsentService: Auto-granted consent for non-EU user');
      }
      
    } catch (error) {
      console.warn('⚠️ PrivacyConsentService: Could not detect location safely, using defaults:', error);
      
      // Safe defaults - assume consent required to be compliant
      this.userLocation = {
        timezone: 'UTC',
        isEU: true, // Default to requiring consent
        requiresConsent: true,
        country: 'Unknown',
        region: 'Unknown'
      };
    }
  }
  
  /**
   * Guess country from timezone
   */
  private guessCountryFromTimezone(timezone: string): string {
    const timezoneCountryMap: { [key: string]: string } = {
      'Europe/London': 'GB',
      'Europe/Paris': 'FR',
      'Europe/Berlin': 'DE',
      'Europe/Rome': 'IT',
      'Europe/Madrid': 'ES',
      'America/New_York': 'US',
      'America/Los_Angeles': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'Asia/Tokyo': 'JP',
      'Asia/Shanghai': 'CN',
      'Australia/Sydney': 'AU'
    };
    
    return timezoneCountryMap[timezone] || 'Unknown';
  }
  
  /**
   * Guess region from timezone
   */
  private guessRegionFromTimezone(timezone: string): string {
    if (timezone.startsWith('Europe/')) return 'Europe';
    if (timezone.startsWith('America/')) return 'Americas';
    if (timezone.startsWith('Asia/')) return 'Asia';
    if (timezone.startsWith('Africa/')) return 'Africa';
    if (timezone.startsWith('Australia/')) return 'Oceania';
    return 'Unknown';
  }
  
  /**
   * Load stored consent settings
   */
  private loadStoredConsent(): void {
    try {
      const stored = localStorage.getItem('privacy_consent_settings');
      if (stored) {
        this.consentSettings = JSON.parse(stored);
        console.log('✅ PrivacyConsentService: Loaded stored consent settings', this.consentSettings);
      }
    } catch (error) {
      console.warn('⚠️ PrivacyConsentService: Could not load stored consent:', error);
    }
  }
  
  /**
   * Save consent settings
   */
  private saveConsentSettings(): void {
    try {
      if (this.consentSettings) {
        localStorage.setItem('privacy_consent_settings', JSON.stringify(this.consentSettings));
        console.log('✅ PrivacyConsentService: Consent settings saved');
      }
    } catch (error) {
      console.error('❌ PrivacyConsentService: Could not save consent settings:', error);
    }
  }
  
  /**
   * Check if consent is required
   */
  public requiresConsent(): boolean {
    return this.userLocation?.requiresConsent ?? true; // Default to requiring consent
  }
  
  /**
   * Check if consent is needed — consent is required AND has not been given yet
   */
  public needsConsent(): boolean {
    return this.requiresConsent() && !this.hasConsent();
  }
  
  /**
   * Check if user has given consent
   */
  public hasConsent(): boolean {
    return this.consentSettings !== null;
  }
  
  /**
   * Get consent settings
   */
  public getConsentSettings(): ConsentSettings | null {
    return this.consentSettings;
  }
  
  /**
   * Set consent settings
   */
  public setConsentSettings(settings: Partial<ConsentSettings>): void {
    this.consentSettings = {
      necessary: true, // Always required
      analytics: settings.analytics ?? false,
      marketing: settings.marketing ?? false,
      functional: settings.functional ?? true,
    };
    
    this.saveConsentSettings();
    
    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('privacy:consent-updated', {
      detail: this.consentSettings
    }));
    
    console.log('✅ PrivacyConsentService: Consent settings updated', this.consentSettings);
  }
  
  /**
   * Set consent (alias for setConsentSettings)
   */
  public setConsent(settings: Partial<ConsentSettings>): void {
    this.setConsentSettings(settings);
  }
  
  /**
   * Get consent (alias for getConsentSettings)
   */
  public getConsent(): ConsentSettings | null {
    return this.getConsentSettings();
  }
  
  /**
   * Get privacy settings
   */
  public getPrivacySettings(): PrivacySettings | null {
    const consent = this.getConsentSettings();
    if (!consent) return null;
    
    // Load additional settings from localStorage
    let additionalSettings = {};
    try {
      const stored = localStorage.getItem('privacy_additional_settings');
      if (stored) {
        additionalSettings = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('⚠️ PrivacyConsentService: Could not load additional privacy settings:', error);
    }
    
    return {
      ...consent,
      dataRetentionPeriod: 365, // Default 1 year
      allowDataSharing: consent.marketing,
      allowPersonalization: consent.functional,
      blockedCategories: [],
      frequencyCap: 10,
      showPersonalizedAds: consent.marketing,
      allowLocationTracking: false,
      allowCrossSiteTracking: false,
      allowThirdPartyData: consent.marketing,
      allowDataCollection: consent.analytics,
      allowCookies: consent.functional,
      ...additionalSettings
    };
  }
  
  /**
   * Set privacy settings
   */
  public setPrivacySettings(settings: PrivacySettings): void {
    this.setConsentSettings({
      analytics: settings.analytics,
      marketing: settings.marketing,
      functional: settings.functional,
      necessary: true
    });
    
    // Store additional privacy settings
    try {
      localStorage.setItem('privacy_additional_settings', JSON.stringify({
        dataRetentionPeriod: settings.dataRetentionPeriod,
        allowDataSharing: settings.allowDataSharing,
        allowPersonalization: settings.allowPersonalization
      }));
    } catch (error) {
      console.error('❌ PrivacyConsentService: Could not save additional privacy settings:', error);
    }
  }
  
  /**
   * Accept all consent
   */
  public acceptAll(): void {
    this.setConsentSettings({
      analytics: true,
      marketing: true,
      functional: true
    });
  }
  
  /**
   * Accept only necessary consent
   */
  public acceptNecessaryOnly(): void {
    this.setConsentSettings({
      analytics: false,
      marketing: false,
      functional: true
    });
  }
  
  /**
   * Revoke consent
   */
  public revokeConsent(): void {
    this.consentSettings = null;
    localStorage.removeItem('privacy_consent_settings');
    
    window.dispatchEvent(new CustomEvent('privacy:consent-revoked'));
    console.log('✅ PrivacyConsentService: Consent revoked');
  }
  
  /**
   * Get user location (safe)
   */
  public getUserLocation(): UserLocation | null {
    return this.userLocation;
  }
  
  /**
   * Get compliance requirements
   */
  public getComplianceRequirements(): any {
    return {
      gdprRequired: this.userLocation?.isEU ?? true,
      ccpaRequired: this.userLocation?.country === 'US',
      consentRequired: this.requiresConsent(),
      dataProcessingLegal: this.hasConsent()
    };
  }
  
  /**
   * Check if analytics is allowed
   */
  public isAnalyticsAllowed(): boolean {
    return this.consentSettings?.analytics ?? false;
  }
  
  /**
   * Check if marketing is allowed
   */
  public isMarketingAllowed(): boolean {
    return this.consentSettings?.marketing ?? false;
  }
  
  /**
   * Check if functional cookies are allowed
   */
  public isFunctionalAllowed(): boolean {
    return this.consentSettings?.functional ?? true;
  }
  
  /**
   * Export user data for GDPR compliance
   */
  public exportUserData(): any {
    return {
      consentSettings: this.consentSettings,
      userLocation: this.userLocation,
      privacySettings: this.getPrivacySettings(),
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
  }
  
  /**
   * Delete user data for GDPR compliance
   */
  public deleteUserData(): void {
    this.revokeConsent();
    localStorage.removeItem('privacy_additional_settings');
    console.log('✅ PrivacyConsentService: User data deleted');
  }
  
  /**
   * Clear all data (alias for deleteUserData)
   */
  public clearAllData(): void {
    this.deleteUserData();
  }
  
  /**
   * Check if ads can be shown
   */
  public canShowAds(): boolean {
    return this.isMarketingAllowed();
  }
  
  /**
   * Check if personalized ads can be shown
   */
  public canShowPersonalizedAds(): boolean {
    return this.isMarketingAllowed();
  }
  
  /**
   * Check if analytics tracking is allowed
   */
  public canTrackAnalytics(): boolean {
    return this.isAnalyticsAllowed();
  }
  
  /**
   * Check if cookies can be used
   */
  public canUseCookies(): boolean {
    return this.isFunctionalAllowed();
  }
}

// Export singleton instance and class
const privacyConsentServiceInstance = PrivacyConsentService.getInstance();
export default privacyConsentServiceInstance;
export { PrivacyConsentService };